/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useState, useEffect } from "react"
import { Drawer } from "antd"
import { Form, Row, Col, Button } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { ResponseWithError } from "types/api"
import { OutletStore } from "types/models/menu"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import notification from "components/common/notification"
import { waitMenuGroupPropagete } from "utils/menu_group"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuStoreList } from "pages/menu"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import useFetchAllMenuGroupLinkedStoreList from "hooks/useFetchAllMenuGroupLinkedStoreList"
import { useHistory } from "react-router"
import { MENUS } from "constants/routes"
import { getGrabXValueFromLocalStorage } from "grabx/getter"
import { MerchantPortalFeatureFlag } from "constants/features"
import {
  menuGroupLinkStoreDrawerButton,
  menuGroupLinkStoreDrawerFooter,
  menuGroupLinkStoreDrawerStyle,
} from "./styles"
import { drawerFooter, drawerTitleStyle } from "../styles"

export interface MenuGroupLinkStoreDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
}

export const MenuGroupLinkStoreDrawerComponent: FC<MenuGroupLinkStoreDrawerProps> = ({
  visible,
  onClose,
}: MenuGroupLinkStoreDrawerProps) => {
  const {
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    menuGroupLinkedStores = [],
  } = useSelector((state: AppState) => state.menu)

  const dispatch = useDispatch()
  const history = useHistory()
  const fetchAllMenuGroupLinkedStoreList = useFetchAllMenuGroupLinkedStoreList()
  const linkedStoreIDs = menuGroupLinkedStores.map(
    menuGroupLinkedStore => menuGroupLinkedStore.value,
  )

  const linkStoreMaxLimit = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.linkStoreMaxLimit,
  )

  const textMap = useTranslationsMap({
    title: "menu.menuGroup.linkedStore.drawer.storeList.drawerTitle",
    menuGroupNameLabel: "menu.menuGroup.drawer.labels.name",
    placeHolderSearchStore: "menu.stores.placeholder.searchByStoreName",
    confirm: "menu.menuGroup.linkedStore.drawer.storeList.confirmButton",
    maxStoreSelectedMessage: "menu.menuGroup.message.maxStoresSelected",
    success: "common.success",
    linkErrorMessageMFC: "menu.menuGroup.message.linkMFC",
    xStoresSelected: "menu.menuGroup.create.xStoreSelected",
  })

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupAddStores,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
          MENU_GROUP_ID: menuGroupID,
        },
      })
    }
  }, [visible, menuGroupID])

  const [selectedStoreIDs, setSelectedStoreIDs] = useState<string[]>([])

  const handleChangeSelectedRowKeys = (newSelectedStoreIDs: string[]) => {
    // filter already linked store IDs
    const filteredNewSelectedStoreIDs = newSelectedStoreIDs.filter(
      newSelectedStoreID => !linkedStoreIDs.includes(newSelectedStoreID),
    )

    if (newSelectedStoreIDs.length > linkStoreMaxLimit) {
      if ([...selectedStoreIDs, ...linkedStoreIDs].length < linkStoreMaxLimit) {
        setSelectedStoreIDs(
          filteredNewSelectedStoreIDs.slice(
            0,
            linkStoreMaxLimit - linkedStoreIDs.length,
          ),
        )
        return
      }

      notification.error({
        message: textMap.maxStoreSelectedMessage.replace(
          "<x>",
          `${linkStoreMaxLimit}`,
        ),
      })
    }

    setSelectedStoreIDs(filteredNewSelectedStoreIDs)
  }

  const getIsDisabledRow = (outlet: OutletStore) => {
    return menuGroupLinkedStores
      .map(menuGroupLinkedStore => menuGroupLinkedStore.value)
      .includes(outlet.merchantID)
  }

  const handleGetCheckboxProps = (outlet: OutletStore) => ({
    disabled: getIsDisabledRow(outlet),
  })

  const handleRowClick = (store: OutletStore) => {
    if (linkedStoreIDs.includes(store.merchantID)) {
      return
    }
    const newSelectedStoreIDs = [...selectedStoreIDs]
    if (newSelectedStoreIDs.indexOf(store.merchantID) >= 0) {
      newSelectedStoreIDs.splice(
        newSelectedStoreIDs.indexOf(store.merchantID),
        1,
      )
    } else {
      newSelectedStoreIDs.push(store.merchantID)
    }
    setSelectedStoreIDs(newSelectedStoreIDs)
  }

  const linkStoreErrorCallback = useCallback(
    (errorResponse: ResponseWithError) => {
      const { message = "" } = errorResponse ?? {}
      if (message.includes("MFC")) {
        return textMap.linkErrorMessageMFC
      }
      return (message.includes("msg=") && message.split("msg=").pop()) || true
    },
    [],
  )

  const handleConfirmClick = useCallback(async () => {
    if (selectedStoreIDs.length) {
      const t0 = performance.now()
      dispatch(actionCreator.global.showLoading())
      const resp: any = await dispatch(
        actionCreator.menu.linkStoreToMenuGroup({
          merchantGroupID,
          menuGroupID,
          storeIDs: selectedStoreIDs,
          errorCallback: linkStoreErrorCallback,
        }),
      )
      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
        NUM_STORES_SELECTED: selectedStoreIDs.length,
        LOADING_STATUS: "SUCCESS",
        LOAD_TIME: 0,
        REASON: "",
        ERROR_MSG: "",
      }
      if (resp?.type?.endsWith("_SUCCESS")) {
        await waitMenuGroupPropagete({ menuGroupID, merchantGroupID, dispatch })
        const updatedMenuGroupID =
          resp?.payload?.data?.menuGroupID || menuGroupID
        if (updatedMenuGroupID !== menuGroupID) {
          history.replace(`${MENUS}/${updatedMenuGroupID}`)
          // refetch menu group
          await dispatch(
            actionCreator.menu.fetchMenuGroupDetail({
              menuGroupID: updatedMenuGroupID,
              merchantGroupID,
            }),
          )
        } else {
          // refetch linked stores
          await fetchAllMenuGroupLinkedStoreList()
        }
        dispatch(
          actionCreator.menu.toggleDrawer({
            showMenuGroupLinkStore: false,
          }),
        )
        dispatch(actionCreator.global.hideLoading())
        notification.success({
          message: textMap.success,
        })
      } else {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }
      const t1 = performance.now()
      labels = {
        ...labels,
        LOAD_TIME: t1 - t0,
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupAddStores,
        action: EventAction.clickConfirm,
        labels,
      })
      dispatch(actionCreator.global.hideLoading())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreIDs, merchantGroupID, menuGroupID])

  useEffect(() => {
    if (!visible) {
      setSelectedStoreIDs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <Fragment>
      <Drawer
        title={
          <Row type="flex" align="middle" gutter={20}>
            <Col
              css={css`
                cursor: pointer;
                display: flex;
                align-items: center;
                padding-right: 0 !important;
              `}
            >
              <ChevronLeftUniversal
                onClick={() => {
                  dispatch(
                    actionCreator.menu.toggleDrawer({
                      showMenuGroupLinkStore: false,
                    }),
                  )
                }}
              />
            </Col>
            <Col css={drawerTitleStyle}>{textMap.title}</Col>
          </Row>
        }
        placement="right"
        width={850}
        visible={visible}
        onClose={onClose}
        destroyOnClose
        css={menuGroupLinkStoreDrawerStyle}
      >
        <MenuStoreList
          data-testid="menu-store-list"
          title={textMap.xStoresSelected.replace(
            "<x>",
            `${
              [...selectedStoreIDs, ...linkedStoreIDs].length
            }/${linkStoreMaxLimit}`,
          )}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: [...selectedStoreIDs, ...linkedStoreIDs],
            onChange: handleChangeSelectedRowKeys,
            getCheckboxProps: handleGetCheckboxProps,
            preserveSelectedRowKeys: true,
          }}
          onRowClickOverride={handleRowClick}
          eventCategory={EventCategory.menuGroupAddStores}
          eventLabels={{
            MERCHANT_GROUP_ID: merchantGroupID,
            MENU_GROUP_ID: menuGroupID,
          }}
        />
        <div
          css={css`
            ${drawerFooter}
            ${menuGroupLinkStoreDrawerFooter}
          `}
        >
          <Button
            css={menuGroupLinkStoreDrawerButton}
            disabled={
              !selectedStoreIDs.length ||
              [...selectedStoreIDs, ...menuGroupLinkedStores].length >
                linkStoreMaxLimit
            }
            data-testid="menu-group-drawer-save-button"
            type="primary"
            onClick={handleConfirmClick}
          >
            {textMap.confirm}
          </Button>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const MenuGroupLinkStoreDrawer = Form.create<
  MenuGroupLinkStoreDrawerProps
>({})(MenuGroupLinkStoreDrawerComponent)
